import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { AuthAside } from "@/pages/Login";
import { cn } from "@/lib/utils";

export const SECTORS = { energie: "Énergie", telecom: "Télécom & internet", assurance: "Assurance", banque: "Banque & crédit", eau: "Eau",
  abonnements: "Abonnements & streaming", immobilier: "Immobilier & logement", logiciels: "Logiciels pro", auto: "Automobile", expertise: "Expertise comptable & conseil", autre: "Autre" };
const FIELDS = [["company", "Raison sociale", "text"], ["siret", "SIRET (14 chiffres, facultatif)", "text"], ["name", "Prénom et nom du contact", "text"],
  ["email", "Email professionnel", "email"], ["phone", "Téléphone", "tel"], ["password", "Mot de passe (8 caractères min.)", "password"]];

export default function PartnerRegister() {
  const { reload } = useAuth();
  const navigate = useNavigate();
  const pre = useSearchParams()[0].get("secteur");
  const tax = pre === "expertise";
  const [form, setForm] = useState({ company: "", siret: "", name: "", email: "", phone: "", password: "", sectors: SECTORS[pre] ? [pre] : [] });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const toggle = (s) => setForm({ ...form, sectors: form.sectors.includes(s) ? form.sectors.filter((x) => x !== s) : [...form.sectors, s] });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.sectors.length) return setError("Choisissez au moins un secteur d'activité");
    setBusy(true); setError("");
    try {
      await api.post("/partners/register", { ...form, siret: form.siret.replace(/\s/g, "") || null });
      await reload();
      navigate("/partenaires", { replace: true });
    } catch (err) { setError(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="grid min-h-screen lg:grid-cols-2" data-testid="partner-register-page">
      <AuthAside />
      <main className="flex flex-col justify-center px-6 py-12 sm:px-12">
        <div className="mx-auto w-full max-w-md fade-up">
          <div className="lg:hidden"><Logo size={40} /></div>
          <h2 className="mt-10 text-3xl font-extrabold lg:mt-0">{tax ? "Expert fiscal partenaire CASHMY" : "Devenir partenaire CASHMY"}</h2>
          {tax && <p className="mt-2 text-sm font-semibold text-primary" data-testid="partner-register-tax-intro">Recevez les demandes de RDV conseil des entreprises qui veulent optimiser leurs charges URSSAF, TVA et impôts.</p>}
          <p className="mt-2 text-sm text-muted-foreground">{tax ? "Chaque demande de RDV conseil est un rendez-vous d'optimisation, sans mise en concurrence de prix : 15 € HT en partagé, 45 € HT en exclusif." : "Recevez des demandes de clients qui veulent payer moins cher, dans vos secteurs. Lead partagé (3 pros) 15 € HT, lead exclusif 45 € HT."} Votre compte est activé après validation par CASHMY.</p>
          <form className="mt-8 space-y-4" onSubmit={submit}>
            {FIELDS.map(([k, l, type]) => (
              <div key={k}><Label htmlFor={`p-${k}`}>{l}</Label>
                <Input id={`p-${k}`} type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required={k !== "siret"}
                  minLength={k === "password" ? 8 : 2} pattern={k === "siret" ? "[0-9 ]{14,17}" : k === "phone" ? "^\\+?[0-9 .\\-]{10,20}$" : undefined}
                  className="mt-1.5 h-11 rounded-xl" data-testid={`partner-register-${k}`} /></div>
            ))}
            <div><Label>Secteurs d'activité</Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">{Object.entries(SECTORS).map(([k, l]) => (
                <button type="button" key={k} onClick={() => toggle(k)} className={cn("rounded-full border px-3 py-1.5 text-xs transition-colors", form.sectors.includes(k) ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted")} data-testid={`partner-sector-${k}`}>{l}</button>
              ))}</div></div>
            {error && <p className="text-sm text-destructive" role="alert" data-testid="partner-register-error">{error}</p>}
            <Button type="submit" disabled={busy} className="h-11 w-full rounded-full" data-testid="partner-register-submit">Envoyer ma candidature<ArrowRight className="ml-2 h-4 w-4" /></Button>
          </form>
          <p className="mt-6 text-center text-sm text-muted-foreground">Déjà partenaire ? <Link to="/login" className="font-semibold text-foreground underline-offset-4 hover:underline" data-testid="partner-go-login">Se connecter</Link></p>
        </div>
      </main>
    </div>
  );
}
